import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { Button, NotificationBox, Panel } from '@rtkwlf/ui';
import { useDispatch } from 'react-redux';
import { Input } from 'reactstrap';
import { AUTH_ERROR_COOKIE_NAME, getCookie } from '../constants';
import { SessionStorageKey, StatusType } from '../types';
import Viewport from '../widgets/Viewport';
import SupportedBrowserAlert from '../widgets/SupportedBrowserAlert';
import { withTrashableContext } from '../hooks';
import { SESSION_ACTIONS } from '../actions/session';
import { devAuth0Login } from '../utils';

const PRIVACY_POLICY_NOTICE = (
  <div className="privacy-policy-notice mt-2">
    By clicking Continue, you agree to Arctic Wolf’s{` `}
    <a
      href="https://arcticwolf.com/privacy-policy-for-customer-portal-users/"
      target="_blank"
      rel="noopener noreferrer"
    >
      Privacy Policy
    </a>
    .
  </div>
);

const getAuth0Error = (): StatusType | null => {
  const reason = getCookie(AUTH_ERROR_COOKIE_NAME) || getErrorMessage();
  if (!reason) {
    return null;
  }

  let message;
  switch (reason) {
    case 'access_denied':
      message =
        'Failed to authenticate: Access denied. Please contact your CST.';
      break;
    case 'lookup_failed':
      message =
        'Failed to authenticate: Service unavailable. Please try again later.';
      break;
    default:
      message =
        'There is a problem with the portal server. Arctic Wolf has been notified. Please try again later.';
  }
  return {
    message,
    color: 'danger',
  };
};

const clearAuth0Error = () => {
  document.cookie = `${AUTH_ERROR_COOKIE_NAME}=;max-age=0;path=/;`;
};

const getLogoutMessage = (): StatusType | null => {
  try {
    return JSON.parse(
      window.sessionStorage.getItem(SessionStorageKey.AUTH_MESSAGE) || '',
    );
  } catch (e) {
    return null;
  }
};

const getErrorMessage = (): string | null => {
  return window.sessionStorage.getItem(SessionStorageKey.LOGIN_ERROR) || '';
};

const clearLogoutMessage = () => {
  window.sessionStorage.removeItem(SessionStorageKey.AUTH_MESSAGE);
};

const useAuth0Login = (username: string) => {
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<StatusType | null>(null);

  const onLogin = React.useCallback(async () => {
    // errors should only be displayed once
    clearAuth0Error();
    clearLogoutMessage();
    setSubmitting(true);

    try {
      // We mock Auth0 for developers so that they dont have to login
      if (process.env.NODE_ENV === 'development') {
        await devAuth0Login(username);
      }

      window.sessionStorage.setItem(
        SessionStorageKey.USE_AUTH0_FRONTEND,
        'true',
      );

      dispatch(SESSION_ACTIONS.SET_LOGIN_EMAIL(username));
    } catch (e) {
      setError({
        message: e.message,
        color: 'danger',
      });
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, username]);

  return {
    error,
    submitting,
    onLogin,
  };
};

const LoginView: React.FunctionComponent = () => {
  const [username, setUsername] = React.useState('');
  const onInputChange = React.useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      setUsername(event.currentTarget.value);
    },
    [],
  );
  const { error, submitting, onLogin } = useAuth0Login(username);

  const onInputKeyUp = React.useCallback(
    async (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && username) {
        await onLogin();
      }
    },
    [onLogin, username],
  );

  const location = useLocation();
  React.useEffect(() => {
    const email = new URLSearchParams(location.search).get('email');
    email && setUsername(email);
    // sync once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = error || getAuth0Error() || getLogoutMessage();

  return (
    <Viewport showBrowserAlert={false}>
      <Panel className="login-panel">
        <div className="p-5">
          <h2 className="text-primary mb-3">
            Log In to the Arctic Wolf Portal
          </h2>

          <NotificationBox
            itemKey="message"
            notificationType="warning"
            allowClose={false}
            className="login-warning-banner"
          >
            <div style={{ display: 'flex' }}>
              <p>
                On February 9, 2022 this page will no longer be needed. Sign in
                will occur through auth.arcticwolf.com. See the preview (right)
                for what to expect.
              </p>
              <img src="/images/login-preview.jpg" alt="Login Preview" />
            </div>
          </NotificationBox>
          <p className="mb-3" style={{ fontSize: '14px' }}>
            Enter your email address below.
          </p>

          <label
            className="min-form-label-width"
            htmlFor="username"
            style={{ marginTop: 0 }}
          >
            Email:
          </label>
          <Input
            data-cy="input-username"
            id="username"
            value={username}
            placeholder="Please enter your email"
            onChange={onInputChange}
            onKeyUp={onInputKeyUp}
            type="text"
            disabled={submitting}
          />

          {status && (
            <div className="d-flex">
              <NotificationBox
                itemKey="message"
                notificationType={
                  status.color === 'danger' ? 'error' : status.color
                }
                allowClose={false}
                className="login-error"
              >
                {status.message}
              </NotificationBox>
            </div>
          )}

          {username && PRIVACY_POLICY_NOTICE}

          <div className="mt-3 mb-n3">
            <SupportedBrowserAlert iconSize={40} isClosable={false} />
          </div>
        </div>

        <div className="login-panel-footer">
          <Button
            data-cy="button-login"
            onClick={onLogin}
            disabled={!username || submitting}
          >
            Continue
          </Button>
        </div>
      </Panel>

      <footer className="page-footer">
        &copy; 2020 Arctic Wolf Networks{' '}
        <span className="d-none d-md-inline">Inc. All Rights Reserved.</span>
      </footer>
    </Viewport>
  );
};

export default withTrashableContext(LoginView);
